/**
 * Mock data for Admin User Management
 */

import type { 
  AdminUser, 
  AdminUserStatus, 
  AdminUserRole,
  PaginatedAdminUsers,
} from '../../types/adminUser';
import { generateId } from './mockData';

const roles: AdminUserRole[] = ['super_admin', 'support', 'investigator'];
const statuses: AdminUserStatus[] = ['active', 'disabled'];

const firstNames = [
  'Alex',
  'Maria',
  'Chen',
  'David',
  'Sarah',
  'James',
  'Emily',
  'Michael',
  'Lisa',
  'Robert',
];

const lastNames = [
  'Johnson',
  'Garcia',
  'Wei',
  'Smith',
  'Williams',
  'Brown',
  'Jones',
  'Davis',
  'Miller',
  'Wilson',
];

const formatDateTime = (date: Date): string => {
  const dateStr = date.toISOString().split('T')[0];
  const timeStr = date.toTimeString().slice(0, 5);
  return `${dateStr} ${timeStr}`;
};

const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

// Generate admin ID like MS-84321
export const generateAdminId = (index: number): string => {
  const num = 84320 + index;
  return `MS-${num}`;
};

// Predefined admin users matching the image
export const predefinedAdminUsers: AdminUser[] = [
  {
    id: generateId(),
    adminId: 'MS-84321',
    name: 'Alex Johnson',
    email: 'alex.j@mispy.ai',
    role: 'super_admin',
    status: 'active',
    lastLogin: new Date('2024-05-20T10:15:00'),
    lastLoginText: '2024-05-20 10:15 AM',
    createdAt: new Date('2023-01-15'),
    createdAtText: '2023-01-15',
  },
  {
    id: generateId(),
    adminId: 'MS-84322',
    name: 'Maria Garcia',
    email: 'maria.g@mispy.ai',
    role: 'support',
    status: 'active',
    lastLogin: new Date('2024-05-19T15:45:00'),
    lastLoginText: '2024-05-19 03:45 PM',
    createdAt: new Date('2023-02-20'),
    createdAtText: '2023-02-20',
  },
  {
    id: generateId(),
    adminId: 'MS-84323',
    name: 'Chen Wei',
    email: 'chen.w@mispy.ai',
    role: 'investigator',
    status: 'disabled',
    lastLogin: new Date('2024-05-15T11:00:00'),
    lastLoginText: '2024-05-15 11:00 AM',
    createdAt: new Date('2023-03-10'),
    createdAtText: '2023-03-10',
  },
  {
    id: generateId(),
    adminId: 'MS-84324',
    name: 'David Smith',
    email: 'david.s@mispy.ai',
    role: 'investigator',
    status: 'active',
    lastLogin: new Date('2024-05-20T09:05:00'),
    lastLoginText: '2024-05-20 09:05 AM',
    createdAt: new Date('2023-04-05'),
    createdAtText: '2023-04-05',
  },
];

// Generate additional mock admin users
const generateMockAdminUser = (index: number): AdminUser => {
  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
  const name = `${firstName} ${lastName}`;
  const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@mispy.ai`;
  const role = roles[Math.floor(Math.random() * roles.length)];
  const status = statuses[Math.floor(Math.random() * statuses.length)];
  const adminId = generateAdminId(10000 + index);

  const now = new Date();
  const daysAgo = Math.floor(Math.random() * 365);
  const createdAt = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

  // Generate last login (some admins never logged in)
  let lastLogin: Date | undefined;
  let lastLoginText: string;
  if (status === 'disabled' || Math.random() < 0.1) {
    lastLogin = undefined;
    lastLoginText = 'Never';
  } else {
    const loginDaysAgo = Math.floor(Math.random() * daysAgo);
    lastLogin = new Date(now.getTime() - loginDaysAgo * 24 * 60 * 60 * 1000);
    lastLoginText = formatDateTime(lastLogin);
  }

  return {
    id: generateId(),
    adminId,
    name,
    email,
    role,
    status,
    lastLogin,
    lastLoginText,
    createdAt,
    createdAtText: formatDate(createdAt),
  };
};

// Generate 12 admin users total (4 predefined + 8 generated)
export const mockAdminUsers: AdminUser[] = [
  ...predefinedAdminUsers,
  ...Array.from({ length: 8 }, (_, i) => generateMockAdminUser(i + 4)),
];

